/**
 * notification-deck.ts — client-side editorial helpers for the Message Center
 * dropdown and `/notifications` page header.
 *
 * The server composes per-item title/body (NotificationBriefComposer). What
 * lives here is the *roll-up* verdict and the (severity, daysUntil) bucketing
 * the dropdown uses for its section headers.
 *
 * Voice rules (mirror posture-brief.ts):
 *  - Verdicts ≤ 6 words.
 *  - No em-dashes. Period-separated clauses.
 *  - Named subjects > raw counts where possible; falls through to
 *    spelled-out small numerals ("Three items.") when we only have counts.
 */

import type {
  NotificationDto,
  NotificationFeed,
  NotificationGroup,
} from '@/types/notifications'

/**
 * Map a notification to its dropdown section.
 *
 *  - `overdue`  — past trigger (`daysUntil <= 0`) AND critical.
 *  - `due-soon` — inside the soon-window (warning + daysUntil <= 14).
 *  - `heads-up` — everything else (low-urgency informational rows).
 *
 * This is intentionally derived rather than carried on the DTO: the server's
 * (severity, daysUntil) pair is already authoritative, and a third enum would
 * just be another thing to drift.
 */
export function groupForNotification(n: NotificationDto): NotificationGroup {
  if (n.severity === 'critical' && n.daysUntil <= 0) return 'overdue'
  if (n.severity === 'warning' && n.daysUntil <= 14) return 'due-soon'
  return 'heads-up'
}

const SMALL_NUMERALS = [
  'No',
  'One',
  'Two',
  'Three',
  'Four',
  'Five',
  'Six',
  'Seven',
  'Eight',
  'Nine',
] as const

/** Spell out 0-9 ("Three items."), digit-render 10+ ("12 items."). Keeps the
 *  verdict line on-voice without making it preachy at scale. Exported so the
 *  page can reuse the same scale for its section decks. */
export function spellSmallNumber(n: number): string {
  if (n < 0) return '0'
  return n < SMALL_NUMERALS.length ? SMALL_NUMERALS[n] : String(n)
}

function plural(n: number, singular: string, plural: string): string {
  return n === 1 ? singular : plural
}

/**
 * Build the dropdown's verdict line from the feed envelope. Strict ≤6-word
 * budget (counted on the longest possible branch). Examples:
 *
 *   - empty feed                  → "All clear."
 *   - 1 item, 0 overdue           → "One item to review."
 *   - 5 items, 2 overdue          → "Five items. Two overdue."
 *   - 3 items, all overdue        → "Three items. All overdue."
 *
 * The verdict is *advisory* — never tells the user what to do, just names
 * the state. Action wording lives on the items themselves.
 */
export function composeNotificationVerdict(feed: NotificationFeed | undefined): string {
  if (!feed || feed.items.length === 0) {
    return 'All clear.'
  }

  const visible = feed.items.filter(item => !item.isDismissed)
  if (visible.length === 0) {
    return 'All clear.'
  }

  const total = visible.length
  const overdue = visible.filter(item => groupForNotification(item) === 'overdue').length

  if (overdue === 0) {
    return `${spellSmallNumber(total)} ${plural(total, 'item to review.', 'items to review.')}`
  }

  if (overdue === total) {
    return `${spellSmallNumber(total)} ${plural(total, 'item.', 'items.')} ${total === 1 ? 'Overdue.' : 'All overdue.'}`
  }

  return `${spellSmallNumber(total)} ${plural(total, 'item.', 'items.')} ${spellSmallNumber(overdue)} overdue.`
}

/**
 * Group + sort items for the dropdown body. Sort within each section by
 * `daysUntil` ascending so the most urgent row of each bucket lands at the
 * top. Caller decides the slice size (the dropdown caps at 8 visible items).
 */
export function partitionByGroup(items: NotificationDto[]): Record<NotificationGroup, NotificationDto[]> {
  const groups: Record<NotificationGroup, NotificationDto[]> = {
    overdue: [],
    'due-soon': [],
    'heads-up': [],
  }
  for (const item of items) {
    if (item.isDismissed) continue
    groups[groupForNotification(item)].push(item)
  }
  for (const key of Object.keys(groups) as NotificationGroup[]) {
    groups[key].sort((a, b) => a.daysUntil - b.daysUntil)
  }
  return groups
}

/**
 * Friendly relative time pill for an item's trigger.
 *  - past         → "5d ago"
 *  - today (0d)   → "today"
 *  - within month → "in 4d"
 *  - further      → "in 6w"
 *
 * Kept tiny on purpose: the timestamp is decoration, not the headline. Anything
 * longer than ~5 characters competes with the dismiss X.
 */
export function relativeFromDays(daysUntil: number): string {
  if (daysUntil === 0) return 'today'
  const abs = Math.abs(daysUntil)
  if (abs <= 30) {
    return daysUntil < 0 ? `${abs}d ago` : `in ${abs}d`
  }
  const weeks = Math.round(abs / 7)
  return daysUntil < 0 ? `${weeks}w ago` : `in ${weeks}w`
}
